import React from 'react';
import { Card, Typography, Tag, Space, Descriptions } from 'antd';
import { GalleryCard, GalleryItem } from './CardGallery';
import { HddOutlined } from '@ant-design/icons';
import nissanLogo from '../assets/nissan_logo.svg';
import gmLogo from '../assets/gm_logo.svg';
import fordLogo from '../assets/ford_logo.svg';
import hondaLogo from '../assets/honda.png';

export interface SystemRowProps extends GalleryItem {
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  status: 'Active' | 'Inactive';
  thumbnailUrl?: string;
}

const getLogo = (systemName: string) => {
  if (!systemName) return null;
  if (systemName.toLowerCase().includes("nissan")) return nissanLogo;
  if (systemName.toLowerCase().includes("gm")) return gmLogo;
  if (systemName.toLowerCase().includes("ford")) return fordLogo;
  if (systemName.toLowerCase().includes("honda")) return hondaLogo;
  return null;
};

// Helper function to determine status color
const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'online':
      return 'green';
    case 'offline':
      return 'red';
    default:
      return 'default';
  }
};

export const SystemCard: React.FC<GalleryCard<SystemRowProps>> = ({ item, onNavigate }) => {
  const statusColor = getStatusColor(item.status);
  const logo = getLogo(item.createdBy);
  
  return (
    <Card
      hoverable
      className="system-card overflow-hidden rounded-lg shadow-md transition-all w-full hover:shadow-lg hover:-translate-y-1"
      styles={{ body: {padding: "16px", height: "100%"} }}
      style={{ 
        borderTop: `2px solid transparent`,
        transition: "all 0.3s ease"
      }}
    >
      <div className="flex flex-col h-full">
        {/* Thumbnail/Icon Section */}
        <div className="h-32 flex items-center justify-center mb-4 bg-gray-50 rounded p-2">
          {logo ? (
            <img 
              src={logo} 
              alt={`${item.createdBy} logo`}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <HddOutlined style={{ fontSize: '48px', opacity: 0.5 }} />
          )}
        </div>
        
        {/* Header with System Name and Status */}
        <div className="mb-4 flex justify-between items-center">
          <Typography.Title level={5} className="m-0" ellipsis={{ tooltip: item.name }}>
            {item.name}
          </Typography.Title>
          <Tag color={statusColor}>{item.status}</Tag>
        </div>
        
        {/* Key Info */}
        <Space direction="vertical" size="small" className="mb-2">
          <div className="flex justify-between">
            <Typography.Text type="secondary">Organization:</Typography.Text>
            <Typography.Text strong>{item.createdBy}</Typography.Text>
          </div>
        </Space>
        
        {/* Description */}
        <div className="mb-4">
          <Typography.Paragraph
            type="secondary"
            ellipsis={{ rows: 2, expandable: false, tooltip: item.description }}
          >
            {item.description || 'No description provided'}
          </Typography.Paragraph>
        </div>
        
        {/* Collapsible Details */}
        <Descriptions
          size="small"
          column={1}
          bordered
          className="mt-2"
          styles={{ 
            label: { fontWeight: "normal" },
            content: { textAlign: "right" }
          }}
        >
          <Descriptions.Item label="Created At">
            {new Intl.DateTimeFormat("en-US", { 
              year: "numeric", 
              month: "2-digit", 
              day: "2-digit", 
              hour: "2-digit", 
              minute: "2-digit" 
            }).format(new Date(item.createdAt))}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {new Intl.DateTimeFormat("en-US", { 
              year: "numeric", 
              month: "2-digit", 
              day: "2-digit", 
              hour: "2-digit", 
              minute: "2-digit" 
            }).format(new Date(item.updatedAt))}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Card>
  );
};